import React from 'react'
import { View, useWindowDimensions } from 'react-native'
import { useSelector } from 'react-redux'
import { StyleService, useStyleSheet } from '@src/style/service'
import { EhrAppointmentKind, EhrAppointmentRecurrenceCadence } from '@src/types'
import { CommonSelect } from '@src/components/CommonSelect'
import { ToggleSwitch } from '@src/components/ToggleSwitch'
import { ehrAppointmentSchedulingInfoSelector } from '../../models/nutritionistHub.selectors'

interface CoachSwitchCallOptionsProps {
  appointmentKind: EhrAppointmentKind
  setAppointmentKind: (kind: EhrAppointmentKind) => void
  cadence: EhrAppointmentRecurrenceCadence
  setCadence: (cadence: EhrAppointmentRecurrenceCadence) => void
  cadenceOptions: { value: EhrAppointmentRecurrenceCadence; text: string }[]
}

const SMALL_DEVICE_WIDTH = 380

export const SchedulingOptions = ({
  appointmentKind,
  setAppointmentKind,
  cadence,
  setCadence,
  cadenceOptions,
}: CoachSwitchCallOptionsProps) => {
  const styles = useStyleSheet(themedStyles)
  const dimensions = useWindowDimensions()

  const isSmallScreen = dimensions.width < SMALL_DEVICE_WIDTH

  const appointmentSchedulingInfo = useSelector(ehrAppointmentSchedulingInfoSelector)
  const schedulingInfo = [
    EhrAppointmentKind.StressManagementMnt,
    EhrAppointmentKind.StressManagement,
  ].includes(appointmentKind)
    ? appointmentSchedulingInfo?.stressManagement
    : appointmentSchedulingInfo?.mnt
  const canBookCall = schedulingInfo?.bookingAvailable
  const appointmentKindOptions = schedulingInfo?.options.map((option) => {
    return {
      value: option.appointmentKind,
      text: `${option.duration} min`,
      recurring: option.recurring,
    }
  })

  const showDurationSelect = appointmentKindOptions ? appointmentKindOptions.length > 1 : false
  const toggleSwitchWithFullWidth = isSmallScreen || !showDurationSelect

  const handleSelectRecurringKind = (value: EhrAppointmentRecurrenceCadence) => {
    setCadence(value)

    if (value === EhrAppointmentRecurrenceCadence.OneTime || !appointmentKindOptions) {
      return
    }

    const currentAppointmentKindOption = appointmentKindOptions.find(
      (option) => option.value === appointmentKind,
    )
    const currentAppointmentKindIsRecurring = currentAppointmentKindOption?.recurring || false
    if (currentAppointmentKindIsRecurring) {
      return
    }

    setAppointmentKind(
      appointmentKindOptions.find((option) => option.recurring)?.value || appointmentKind,
    )
  }

  const renderSelectComponent = () => {
    if (!showDurationSelect) {
      return null
    }

    return (
      <View style={[styles.selectWrapper, isSmallScreen && styles.selectWrapperSmall]}>
        <CommonSelect
          selectedOption={appointmentKindOptions?.find(
            (option) => option.value === appointmentKind,
          )}
          options={appointmentKindOptions}
          onSelect={(option) => setAppointmentKind(option.value)}
          appearance="rounded"
          size="small"
          disabled={cadence !== EhrAppointmentRecurrenceCadence.OneTime || !canBookCall}
        />
      </View>
    )
  }

  const toggleComponent = (
    <View style={!toggleSwitchWithFullWidth && styles.toggleWrapper}>
      <ToggleSwitch
        options={cadenceOptions}
        selectedOption={cadenceOptions.find((option) => option.value === cadence)}
        onSelect={(option) =>
          handleSelectRecurringKind(option.value as EhrAppointmentRecurrenceCadence)
        }
      />
    </View>
  )

  return (
    <View style={[styles.container, toggleSwitchWithFullWidth && styles.containerSmall]}>
      {toggleSwitchWithFullWidth ? (
        <>
          {toggleComponent}
          {renderSelectComponent()}
        </>
      ) : (
        <>
          {renderSelectComponent()}
          {toggleComponent}
        </>
      )}
    </View>
  )
}

const themedStyles = StyleService.create({
  container: {
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
  },
  containerSmall: {
    flexDirection: 'column',
    gap: 12,
  },
  selectWrapper: {
    flex: 1,
    maxWidth: 110,
  },
  selectWrapperSmall: {
    flex: 0,
    width: '100%',
    maxWidth: '100%',
  },
  toggleWrapper: {
    flex: 1,
  },
  optionsContainer: {
    flex: 1,
  },
  confirmButton: {
    marginLeft: 'auto',
  },
  badge: {
    backgroundColor: 'theme.surface.base2',
    paddingHorizontal: 16,
    paddingVertical: 3,
    borderRadius: 32,
    textAlign: 'center',
  },
})
